import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { VideoFirstHeroDataQuery, VideoFirstHeroDataQueryVariables } from './api.graphql';
import { VideoFirstHeroData as VideoFirstHeroDataQueryDocument } from './api.graphql';
import { getTikTokPlayerUrl } from './source';

/**
 * The social-media creator the petition angles were generated from — i.e. the TikTok
 * account that posted the video, not the Change.org petition starter.
 */
export type VideoFirstHeroCreator = Readonly<{
	name?: string;
	username?: string;
}>;

export type VideoFirstHeroData =
	| Readonly<{ enabled: false }>
	| Readonly<{ enabled: true; sourceUrl: string; creator: VideoFirstHeroCreator }>;

const DISABLED_VIDEO_FIRST_HERO = { enabled: false } as const;

type QueryMetadata = NonNullable<
	NonNullable<NonNullable<VideoFirstHeroDataQuery['petition']>['socialStarterPetitionAngles']>['metadata']
>;

function toCreator(metadata: QueryMetadata | null | undefined): VideoFirstHeroCreator {
	return {
		name: metadata?.name ?? undefined,
		username: metadata?.username ?? undefined,
	};
}

export async function getVideoFirstHeroData(
	utilityContext: UtilityContext,
	slugOrId: string,
): Promise<VideoFirstHeroData> {
	try {
		const {
			data: { petition },
		} = await utilityContext.gql.fetch<VideoFirstHeroDataQuery, VideoFirstHeroDataQueryVariables>({
			batched: false, // to ensure the rate limited 'socialStarterPetitionAngles' field is not batched
			query: VideoFirstHeroDataQueryDocument,
			variables: { slugOrId },
			path: '/video-first-hero',
			rejectOnError: true,
		});
		const metadata = petition?.socialStarterPetitionAngles?.metadata;
		const sourceUrl = metadata?.sourceUrl ?? undefined;

		return sourceUrl && getTikTokPlayerUrl(sourceUrl)
			? {
					enabled: true,
					sourceUrl,
					creator: toCreator(metadata),
				}
			: DISABLED_VIDEO_FIRST_HERO;
	} catch (error) {
		void utilityContext.errorReporter.report(error as Error, { severity: 'warning' });
		return DISABLED_VIDEO_FIRST_HERO;
	}
}
